/*
 * Refactored TypeScript version of the Observer library.
 * Maintains the same public API and window usage.
 */

// Define callback function signatures with proper generics
type SubscriberCallback<
  TData = undefined,
  TContext = undefined,
  TResult = void,
> = (this: TContext, data: TData) => TResult;

type HandlerCallback<TResult = void> = (result: TResult) => void;

interface Subscription<
  TData = undefined,
  TContext = undefined,
  TResult = void,
> {
  fn: SubscriberCallback<TData, TContext, TResult>;
  context: TContext;
  received: number[];
}

interface QueuedTopic<TData = undefined, TResult = void> {
  topic: string;
  data: TData;
  publishId: number;
  handler?: HandlerCallback<TResult>;
}

interface ObserverMemory {
  queued: Record<string, Array<QueuedTopic<unknown>>>;
  topicSubscriptions: Record<string, Array<Subscription<unknown>>>;
}

declare global {
  interface Window {
    Sb?: {
      ObserverMemory?: ObserverMemory;
      [key: string]: unknown;
    };
  }
}

let queued: Record<string, Array<QueuedTopic<unknown>>> = {};
let topicSubscriptions: Record<string, Array<Subscription<unknown>>> = {};

const init = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  if (!window.Sb) {
    window.Sb = {};
  }
  if (window.Sb.ObserverMemory) {
    ({ queued, topicSubscriptions } = window.Sb.ObserverMemory);
  } else {
    queued = {};
    topicSubscriptions = {};
    window.Sb.ObserverMemory = { queued, topicSubscriptions };
  }
};

const process = (topic: string): void => {
  const subscriptions = topicSubscriptions[topic];
  if (!subscriptions?.length) {
    return;
  }
  const queuedTopics = queued[topic];
  if (!queuedTopics?.length) {
    return;
  }

  for (let i = 0, lenTopics = queuedTopics.length; i < lenTopics; i += 1) {
    const queuedTopic = queuedTopics[i];
    const lenSubscribers = subscriptions.length;
    for (let j = 0; j < lenSubscribers; j += 1) {
      const subscription = subscriptions[j];
      setTimeout(() => {
        try {
          if (!subscription.received.includes(queuedTopic.publishId)) {
            const res = subscription.fn.call(
              subscription.context,
              queuedTopic.data,
            );
            subscription.received.push(queuedTopic.publishId);
            if (queuedTopic.handler) {
              queuedTopic.handler.apply(res);
            }
          }
        } catch (err) {
          throw new Error(
            `An error occurred while handling event topic '${topic}'. Error: ${
              err instanceof Error ? err.stack || err.message : String(err)
            }`,
          );
        }
      }, 0);
    }
  }
};

let counter = 0;

const observer = {
  subscribe<TData = undefined, TContext = undefined, TResult = void>(
    topic: string,
    fn: SubscriberCallback<TData, TContext, TResult>,
    context?: TContext,
  ): [string, SubscriberCallback<TData, TContext, TResult>] {
    if (!topicSubscriptions[topic]) {
      topicSubscriptions[topic] = [];
    }
    const subscribers = topicSubscriptions[topic];
    subscribers.push({
      fn,
      context: (context !== undefined ? context : this) as TContext,
      received: [],
    } as unknown as Subscription<unknown>);
    process(topic);
    return [topic, fn];
  },

  publish<TData = undefined, TResult = void>(
    topic: string,
    data?: TData,
    handler?: HandlerCallback<TResult>,
  ) {
    const publishId = Math.floor(Math.random() * 1000000) * 10000 + counter;
    counter += 1;
    if (!queued[topic]) {
      queued[topic] = [];
    }
    queued[topic].push({
      topic,
      data,
      publishId,
      handler,
    } as QueuedTopic<unknown>);
    process(topic);
    return observer;
  },

  unsubscribe<TData = undefined, TContext = undefined, TResult = void>(
    handle?: [string, SubscriberCallback<TData, TContext, TResult>],
  ): void {
    if (!handle) {
      return;
    }
    if (!Array.isArray(handle)) {
      throw new Error(`Invalid handle: ${handle}`);
    }
    const topic = handle[0];
    const callback = handle[1];
    const subscribers = topicSubscriptions[topic];
    if (!subscribers) {
      return;
    }
    let len = subscribers.length;
    while (len > 0) {
      len -= 1;
      // Use type assertion to make the comparison work
      if (
        subscribers[len].fn ===
        (callback as unknown as SubscriberCallback<unknown>)
      ) {
        subscribers.splice(len, 1);
      }
    }
  },

  wrap(prefix: string, obj: Record<string, SubscriberCallback>): void {
    Object.keys(obj).forEach((key) => {
      observer.subscribe(`${prefix}${key}`, obj[key]);
    });
  },

  init,
};

init();

export default observer;
